import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useProgressNotePointOfCareForm } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useProgressNotePointOfCareForm"
import { useProgressNotePointOfCareFormEffect } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useProgressNotePointOfCareFormEffect"
import {
  ChPointOfCareSchema,
  DePointOfCareSchema,
  type PointOfCareFormType,
} from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { REQUIRED_CARE_PROVIDER } from "@/modules/patients/detail/progress-note/point-of-care/schema/ch-schema"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { getInsureeInfo } from "@/modules/patients/libs/get-insuree-info"
import type { Country } from "@/types/__generated__/union-types"

export const useInitializePointOfCare = (
  country: Country,
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData,
) => {
  const patientData = usePatientFileContext((state) => state.patientData)
  const insuree = getInsureeInfo(patientData?.insurees)
  const patientGatekeeper = insuree?.coverage?.product.productConfiguration?.gatekeeper ?? null
  const pointOfCareSchema = country === "CH" ? ChPointOfCareSchema(patientGatekeeper) : DePointOfCareSchema

  const { defaultValues, storeFormState } = useProgressNotePointOfCareForm(
    episodeOfCareProgressNoteData.id,
    episodeOfCareProgressNoteData.pointOfCare,
    episodeOfCareProgressNoteData.betterDoc,
  )

  const form = useForm<PointOfCareFormType>({
    defaultValues,
    resolver: zodResolver(pointOfCareSchema),
  })

  const hasCareProvider = !!(patientGatekeeper?.length && REQUIRED_CARE_PROVIDER.includes(patientGatekeeper))

  useProgressNotePointOfCareFormEffect(episodeOfCareProgressNoteData, storeFormState, form)

  return { form, hasCareProvider }
}
